/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Observable } from "@vienna-hypertext/observable";
import Cookies from "js-cookie";
import { isElectron } from "react-device-detect";

import { API } from "src/classes/api";
import { CurrentUser } from "src/classes/user/CurrentUser";
import { StringUtils } from "src/classes/utilities/StringUtils";
import { StageName } from "src/components/Signup/Signup";
import { AUTH_ROUTE_MAP, SignupMethod } from "src/lib/auth";
import { AuthLevel } from "src/lib/constants";

type AuthSnapshot = {
  authLevel: AuthLevel;
};

type EmailLoginArgs = {
  email: string;
  password: string;
};

type UrbitLoginArgs = {
  shipName: string;
  urbitKey: string;
};

type LoginArgs = EmailLoginArgs | UrbitLoginArgs;
export class Auth extends Observable<AuthSnapshot> {
  public static readonly instance = Auth.create(); // Singleton pattern. Alternatively, could use useContext with no value provider to do essentially the same thing.

  public user: CurrentUser | null = null;

  private constructor(snapshot: AuthSnapshot) {
    super(snapshot);
  }

  /* -------------------------------------------------- */
  /*                       Login                        */
  /* -------------------------------------------------- */
  public async login(method: SignupMethod, args: LoginArgs): Promise<void> {
    let token = "";
    switch (method) {
      case SignupMethod.Email:
        const { email, password } = args as EmailLoginArgs;
        token = await API.auth.signIn(email, password);
        break;
      case SignupMethod.Urbit:
        const { shipName, urbitKey } = args as UrbitLoginArgs;
        token = await API.auth.signInWithUrbit(shipName, urbitKey);
    }
    Auth.writeToken(token);
    await this.authenticate();
  }

  public async authenticate(): Promise<void> {
    const authLevel = Auth.getAuthLevelFromToken();
    this.setState({ authLevel });
    if (authLevel === AuthLevel.Authorized) {
      const res = await API.auth.getCurrentUser();
      Auth.writeToken(res.token);
      this.user = res.user;
    }
  }

  public async completeOnboarding(): Promise<Record<string, any>> {
    const token = await API.auth.completeOnboarding();
    Auth.writeToken(token);
    this.setState({ authLevel: Auth.getAuthLevelFromToken() });
    return StringUtils.parseToken(token);
  }

  /* -------------------------------------------------- */
  /*                       Logout                       */
  /* -------------------------------------------------- */

  public logout(): void {
    this.user?.onLogout();
    this.setState({ ...Auth.newSnapshot() });
    Auth.clearLocalStorage();
    this.user = null;
  }

  /* -------------------------------------------------- */
  /*                        Token                       */
  /* -------------------------------------------------- */
  public static getAuthLevelFromToken(): AuthLevel {
    const token = Auth.readToken();
    if (JSON.stringify(token) === JSON.stringify({})) {
      return AuthLevel.Guest;
    }
    if (token.onboardingComplete) {
      return AuthLevel.Authorized;
    }
    return AuthLevel.Onboarding;
  }

  public static readToken(): Record<string, any> {
    const token = localStorage.getItem("token") || "";
    const parsedToken = StringUtils.parseToken(token);
    return parsedToken;
  }

  public static readRawToken(): string {
    const token = localStorage.getItem("token") || "";
    return token;
  }

  public static writeToken(token: string): void {
    localStorage.setItem("token", token);
    // Set cookie after first login so we know to redirect to /home/mine instead of meta
    Cookies.set("token", token, { expires: 365 });
    if (isElectron) {
      window.electronAPI?.authAPI.setToken(token);
    }
  }

  public static removeToken(): void {
    localStorage.removeItem("token");
    Cookies.remove("token");
    if (isElectron) {
      window.electronAPI?.authAPI.setToken("");
    }
  }

  public static clearLocalStorage(): void {
    localStorage.clear();
  }

  public static writeUrbitKey(key: string): void {
    localStorage.setItem("urbitKey", key);
  }

  public static readUrbitKey(): string {
    return localStorage.getItem("urbitKey") || "";
  }

  public static writeLastOnboardingStage(stage: StageName): void {
    localStorage.setItem("lastOnboardingStage", stage);
  }

  public static readLastOnboardingStage(): StageName | null {
    const stage = localStorage.getItem("lastOnboardingStage");
    return stage as StageName | null;
  }

  /* -------------------------------------------------- */
  /*                       Routing                      */
  /* -------------------------------------------------- */

  public isAuthorizedForRoute(route: string): boolean {
    const authStatus = this.snapshot.authLevel;
    return AUTH_ROUTE_MAP[authStatus].allowedRoutes.includes(route);
  }

  public get defaultRoute(): string {
    const authStatus = this.snapshot.authLevel as AuthLevel.Guest | AuthLevel.Authorized;
    return AUTH_ROUTE_MAP[authStatus].defaultRoute;
  }

  /* -------------------------------------------------- */
  /*                        Create                      */
  /* -------------------------------------------------- */
  private static create(): Auth {
    const snapshot = Auth.newSnapshot();
    return new Auth(snapshot);
  }

  private static newSnapshot(): AuthSnapshot {
    const snapshot: AuthSnapshot = {
      authLevel: AuthLevel.Guest,
    };
    return snapshot;
  }
}
